// components/ProductImage.jsx
import React, { useState } from 'react';
import { Box, CardMedia, Button, Chip } from '@mui/material';
import { Visibility as VisibilityIcon, ViewInAr as ViewInArIcon } from '@mui/icons-material';

const ProductImage = ({ product, language, onQuickView, onARView, noPhoto }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const isOutOfStock = product.quantity <= 0;
  const hasDiscount = product.discount_percent > 0;
  const hasARView = product.has_ar_view || product.ar_model_path;

  return (
    <Box
      sx={{
        position: "relative",
        height: { xs: 180, sm: 200, md: 210 },
        overflow: "hidden",
        backgroundColor: '#1A1A1A',
        borderRadius: '12px 12px 0 0',
      }}
    >
      {/* Loading Skeleton */}
      {!imageLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#1A1A1A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '2px solid #2D2D2D',
              borderTopColor: '#FFD700',
              animation: 'spin 0.8s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        </Box>
      )}
      
      <CardMedia
        component="img"
        image={product.imageUrl || noPhoto}
        alt={product.name}
        onLoad={() => setImageLoaded(true)}
        sx={{
          height: "100%",
          width: "100%",
          objectFit: "cover",
          transition: "transform .5s cubic-bezier(0.4, 0, 0.2, 1)",
          opacity: imageLoaded ? 1 : 0,
          "&:hover": {
            transform: "scale(1.08)",
          },
        }}
      />
      
      {/* Badges */}
      <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2, display: 'flex', gap: 1 }}>
        {hasDiscount && (
          <Chip
            label={`-${product.discount_percent}%`}
            size="small"
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 24,
            }}
          />
        )}
      </Box>
      
      {isOutOfStock && (
        <Chip
          label={language === 'ar' ? 'غير متوفر' : 'Out of Stock'}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: '#FF3D00',
            border: '1px solid #FF3D00',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 24,
            backdropFilter: 'blur(4px)',
          }}
        />
      )}
      
      {/* Hover Overlay */}
      {!isOutOfStock && (
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
            p: 2,
            '&:hover': {
              opacity: 1
            },
          }}
        >
          {hasARView && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ViewInArIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onARView?.();
              }}
              fullWidth
              sx={{
                borderColor: '#FFD700',
                color: '#FFD700',
                borderRadius: '30px',
                py: 0.8,
                fontSize: '0.75rem',
                fontWeight: 600,
                backdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(0,0,0,0.3)',
                '&:hover': {
                  borderColor: '#FFE55C',
                  backgroundColor: 'rgba(255, 215, 0, 0.15)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {language === 'ar' ? 'عرض ثلاثي الأبعاد' : '3D View'}
            </Button>
          )}
          
          <Button
            variant="contained"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onQuickView?.();
            }}
            fullWidth
            sx={{
              borderRadius: '30px',
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 700,
              fontSize: '0.75rem',
              py: 0.8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: '#FFE55C',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
              },
            }}
          >
            {language === 'ar' ? 'عرض سريع' : 'Quick View'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProductImage;